import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import * as PIXI from "pixi.js";
import { Howler } from "howler";
import { useSelector } from "react-redux";
import { soundManager } from "../SoundManager";
import {
  selectPotCollectionActive,
  selectAutoPlayActive,
  selectIsSpinning,
  selectAnyFeatureTriggeredOrActive,
} from "../State/selectors";

export const AppActivityContext = createContext(null);

// ── Spin idle timer ──
// After SPIN_IDLE_INTERVAL ms of no spin activity, trigger ambient music crossfade
const SPIN_IDLE_INTERVAL = 30000; // 30 seconds

/**
 * AppActivityProvider — Production-grade tab visibility management
 *
 * Provides `isActive` state that tracks whether the browser tab is visible.
 * When the tab is hidden:
 *   - Audio is muted via Howler
 *   - Pixi ticker speed is set to 0 (freezes rendering without killing the loop)
 *
 * Also tracks spin idle state:
 *   - `registerSpin()` — call when a spin starts to reset the idle timer
 *   - `spinIdleTick` — counter that increments every 30s of no spin activity
 *
 * Design decisions:
 *   1. Uses ONLY `visibilitychange` — `blur`/`focus` cause false positives
 *      (DevTools, alerts, iframes). `pagehide`/`freeze`/`pageshow` are redundant
 *      as they always fire alongside or after `visibilitychange`.
 *   2. Uses `ticker.speed = 0` on BOTH app.ticker and Ticker.shared instead of
 *      `ticker.stop()` — stop() kills the loop and spikes delta on restart.
 *      speed=0 keeps the loop alive with deltaMS=0 so spins, TickerTimeouts,
 *      and spine all freeze cleanly and resume without wall-clock drift.
 *   3. 100ms debounce prevents rapid state flicker from quick tab switching
 *      or edge cases where visibility events fire multiple times.
 */
export const AppActivityProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(true);
  const [spinIdleTick, setSpinIdleTick] = useState(0);
  const spinIdleTimerRef = useRef(null);

  // ── "Playing" as the timer itself sees it ──
  //
  // A ref because `startIdleTimer` is a stable callback: it is attached to the
  // DOM listeners once, at mount, and must read the CURRENT answer when it
  // fires rather than the one it closed over. Assigned below, where the value
  // is worked out.
  const gameIsPlayingRef = useRef(false);

  // Start (or restart) the repeating idle timer
  const startIdleTimer = useCallback(() => {
    if (spinIdleTimerRef.current) clearInterval(spinIdleTimerRef.current);
    spinIdleTimerRef.current = null;

    // ── NEVER while the game is playing ──
    //
    // Everything that wants to restart this clock comes through here — the
    // mount, `registerSpin`, and the pointerdown/keydown listener — so this is
    // the one place that can refuse them all.
    //
    // The listener is what made it matter. It restarts the clock on ANY tap,
    // and the effect below only clears the interval when `gameIsPlaying`
    // CHANGES. So a tap during a free-spins round — skipping a win, or just
    // touching the screen — started an interval nothing would cancel, and 30s
    // later it crossfaded to ambient over a round that was still playing.
    // Vladimir, 2026-09-22: "it literally turned off the free spins music and
    // played ambient music even though the game played actively."
    //
    // Refusing rather than deferring: when the round ends, the effect below
    // calls this again with the answer now false, which is a fresh full
    // interval — and that is what a round that just finished should buy.
    if (gameIsPlayingRef.current) return;

    spinIdleTimerRef.current = setInterval(() => {
      setSpinIdleTick((prev) => {
        const next = prev + 1;
        // Crossfade to ambient on first tick (30s of inactivity)
        if (next === 1) {
          soundManager.crossfadeToAmbient();
        }
        return next;
      });
    }, SPIN_IDLE_INTERVAL);
  }, []);

  // Called by consumers when a spin happens — resets the idle cycle
  const registerSpin = useCallback(() => {
    setSpinIdleTick(0);
    startIdleTimer();
    // Crossfade back from ambient to background music
    soundManager.crossfadeToBackground();
  }, [startIdleTimer]);

  // Start initial idle timer on mount + global interaction listener
  useEffect(() => {
    startIdleTimer();

    // Any user interaction (click/tap/keypress) resets idle and restores background music
    const handleInteraction = () => {
      setSpinIdleTick((prev) => {
        if (prev > 0) {
          soundManager.crossfadeToBackground();
        }
        return 0;
      });
      startIdleTimer();
    };

    document.addEventListener('pointerdown', handleInteraction);
    document.addEventListener('keydown', handleInteraction);

    return () => {
      if (spinIdleTimerRef.current) clearInterval(spinIdleTimerRef.current);
      document.removeEventListener('pointerdown', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [startIdleTimer]);

  // ── A GAME THAT IS PLAYING IS NOT IDLE ──
  //
  // This used to ask one question: is Pot Collection running? That is Zeus's
  // bonus, and `POT_COLLECTION.ENABLED` is false in Mad Labs — so the answer
  // was always no, and the 30s clock ran through everything. A free-spins room
  // plays itself, so nobody touches the screen, and after 30s the music
  // crossfaded to ambient in the middle of the bonus. Reported 2026-09-18.
  //
  // `registerSpin()` was supposed to cover this and cannot: its only caller is
  // BackgroundAnimation.jsx, which this game never renders — GameView replaces
  // it when GAME_VIEW.ENABLED is true. So nothing but a literal tap or
  // keypress was resetting the clock.
  //
  // ── The same predicate the LICENCE rule already uses ──
  //
  // IdleLogoutManager decides the 20-minute logout with
  // `autoPlayActive || isSpinning || featureActive`, from Vladimir's own rule
  // on 2026-09-10: "if the game is running and playing I'm not inactive". The
  // question here is identical, so the answer is copied rather than invented —
  // one definition of "playing" for the licence and for the music.
  //
  // Pot Collection stays in the list. It costs nothing while disabled and
  // keeps this correct for Zeus, which shares the file.
  const potCollectionActive = useSelector(selectPotCollectionActive);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const isSpinning = useSelector(selectIsSpinning);
  const featureActive = useSelector(selectAnyFeatureTriggeredOrActive);
  const gameIsPlaying =
    autoPlayActive || isSpinning || featureActive || potCollectionActive;
  // Read by startIdleTimer at fire time — see the ref's own note.
  gameIsPlayingRef.current = gameIsPlaying;
  useEffect(() => {
    if (gameIsPlaying) {
      // Playing — clear the idle timer so the ambient crossfade cannot fire.
      if (spinIdleTimerRef.current) {
        clearInterval(spinIdleTimerRef.current);
        spinIdleTimerRef.current = null;
      }
      // And if it already fired, come back from ambient.
      setSpinIdleTick(0);
      soundManager.crossfadeToBackground();
    } else {
      // Idle again — restart the clock fresh rather than resuming it, so a
      // round that just ended buys the player the full interval.
      startIdleTimer();
    }
  }, [gameIsPlaying, startIdleTimer]);

  useEffect(() => {
    let debounceTimer = null;

    const handleVisibilityChange = () => {
      const active = document.visibilityState === "visible";

      // Debounce: prevent rapid state flicker from quick tab switching
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        setIsActive((prev) => {
          if (prev === active) return prev;

          // Mute/unmute audio
          Howler.mute(!active);

          // Freeze/unfreeze Pixi tickers without killing the loop.
          // speed = 0 → deltaMS = 0 for all subscribers (spins, TickerTimeouts,
          // spine). Must set BOTH app.ticker and Ticker.shared — game logic
          // and TickerTimeout use shared; the stage renders on app.ticker.
          const speed = active ? 1 : 0;
          const app = globalThis.__PIXI_APP__;
          if (app && app.ticker) {
            app.ticker.speed = speed;
          }
          PIXI.Ticker.shared.speed = speed;

          return active;
        });
      }, 100);
    };

    // Initialize with current state
    handleVisibilityChange();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, []);

  // Stable context value — only re-creates when isActive or spinIdleTick changes
  const value = useMemo(
    () => ({
      isActive,
      spinIdleTick,
      registerSpin,
    }),
    [isActive, spinIdleTick, registerSpin]
  );

  return (
    <AppActivityContext.Provider value={value}>
      {children}
    </AppActivityContext.Provider>
  );
};

// Hook for consuming the context
export const useAppActivity = () => {
  const context = useContext(AppActivityContext);
  if (!context) {
    throw new Error(
      "useAppActivity must be used within an AppActivityProvider"
    );
  }
  return context;
};
